import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiService';
import { usePortfolioContext } from './PortfolioContext';
import { cn } from '../../lib/utils';
import { useNumberLocale } from '../../utils/numberFormat';
import { parseServerTimestamp } from '../../utils/dateFormat';

// Map event types to human-readable labels
const EVENT_TYPE_LABELS: Record<string, { label: string; icon: string; color: string }> = {
    'scenario_update_modeldata_value_float': { label: 'Value changed', icon: 'edit', color: 'text-blue-600' },
    'scenario_update_modeldata_value_bool': { label: 'Toggle changed', icon: 'toggle_on', color: 'text-blue-600' },
    'scenario_update_modeldata_value_date': { label: 'Date changed', icon: 'event', color: 'text-blue-600' },
    'scenario_update_modeldata_value_enum': { label: 'Selection changed', icon: 'list', color: 'text-blue-600' },
    'scenario_update_modeldata_value_integer': { label: 'Value changed', icon: 'edit', color: 'text-blue-600' },
    'scenario_update_modeldata_value_str': { label: 'Text changed', icon: 'edit_note', color: 'text-blue-600' },
    'scenario_update_modeldata_value_time_serie': { label: 'Time series updated', icon: 'timeline', color: 'text-purple-600' },
    'scenario_update_modeldata_value_time_serie_elem': { label: 'Time series element updated', icon: 'timeline', color: 'text-purple-600' },
    'scenario_update_modeldata_value_ts_timing': { label: 'Timing changed', icon: 'schedule', color: 'text-amber-600' },
    'scenario_update_modeldata_value_relation': { label: 'Relation changed', icon: 'link', color: 'text-green-600' },
    'scenario_update_modeldata_value_lst_elem_add': { label: 'List item added', icon: 'add_circle', color: 'text-green-600' },
    'scenario_update_modeldata_value_lst_elem_update': { label: 'List item updated', icon: 'edit', color: 'text-blue-600' },
    'scenario_update_modeldata_value_lst_elem_delete': { label: 'List item removed', icon: 'remove_circle', color: 'text-red-600' },
    'scenario_create_modeldata': { label: 'Module created', icon: 'add_box', color: 'text-green-600' },
    'scenario_delete_modeldata': { label: 'Module deleted', icon: 'delete', color: 'text-red-600' },
    'scenario_rename_modeldata': { label: 'Module renamed', icon: 'drive_file_rename_outline', color: 'text-amber-600' },
    'scenario_rename': { label: 'Scenario renamed', icon: 'badge', color: 'text-amber-600' },
    'scenario_update_modeldata_value_timing_reference': { label: 'Timing reference changed', icon: 'schedule', color: 'text-amber-600' },
    'scenario_update_modeldata_value_customizable_timing_reference': { label: 'Custom timing changed', icon: 'schedule', color: 'text-amber-600' },
};

function snakeToPascal(str: string): string {
    if (!str) return '';
    return str.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Backend bakes relation references as "Name (#123)" — strip the id suffix for display
const RELATION_ID_SUFFIX = /\s*\(#\d+\)\s*$/;

function formatValue(value: any, locale: string): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number') return value.toLocaleString(locale, { maximumFractionDigits: 4 });
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') return value.replace(RELATION_ID_SUFFIX, '');
    if (Array.isArray(value)) {
        if (value.length === 0) return '[]';
        return `[${value.map(v => formatValue(v, locale)).join(', ')}]`;
    }
    if (typeof value === 'object') {
        // Common shapes: { name }, { display_name }, { label }, { value } — prefer those for a compact display
        if (typeof value.name === 'string') return value.name;
        if (typeof value.display_name === 'string') return value.display_name;
        if (typeof value.label === 'string') return value.label;
        if ('value' in value && typeof value.value !== 'object') return formatValue(value.value, locale);
        // Fallback: render as "Field A: x, Field B: y" using snakeToPascal for keys
        // Skip "id" keys (relation/list-element identifiers) — they are backend-only and noisy for users
        const entries = Object.entries(value).filter(
            ([k, v]) => k !== 'id' && v !== null && v !== undefined && v !== ''
        );
        if (entries.length === 0) return '—';
        return entries.map(([k, v]) => `${snakeToPascal(k)}: ${formatValue(v, locale)}`).join(', ');
    }
    return String(value);
}

function UserInitials({ name }: { name: string }) {
    const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    return (
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold shrink-0">
            {initials}
        </div>
    );
}

interface Props {
    initialAssetId?: number | null;
    onClose: () => void;
}

export default function StalenessReview({ initialAssetId, onClose }: Props) {
    const { selectedGroup, selectGroup, computeGroup, refreshGroups, canWrite } = usePortfolioContext();

    // View mode: 'group' = all assets, or asset_id = filtered to one asset
    const [viewAssetId, setViewAssetId] = useState<number | null>(initialAssetId ?? null);

    // Data
    const [staleness, setStaleness] = useState<any>(null);
    const [approvalLog, setApprovalLog] = useState<any[]>([]);
    const [logTotalCount, setLogTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingLog, setLoadingLog] = useState(false);

    // Pending changes for stale assets
    const [pendingChanges, setPendingChanges] = useState<Record<number, any[]>>({});
    const [loadingPending, setLoadingPending] = useState<number | null>(null);
    const [expandedPendingAssetId, setExpandedPendingAssetId] = useState<number | null>(null);

    // Expanded approval entries
    const [expandedApprovalId, setExpandedApprovalId] = useState<number | null>(null);

    const [refreshingAssetId, setRefreshingAssetId] = useState<number | null>(null);

    useEffect(() => {
        if (!selectedGroup) return;
        fetchData();
    }, [selectedGroup?.id, viewAssetId]);

    const fetchData = async () => {
        if (!selectedGroup) return;
        setLoading(true);
        setLoadingLog(true);
        try {
            // Fetch staleness
            const stalenessRes = await apiService.getPortfolioStaleness(selectedGroup.id);
            if (stalenessRes?.ok) setStaleness(await stalenessRes.json());

            // Fetch approval log
            let logRes;
            if (viewAssetId) {
                logRes = await apiService.getAssetApprovalLog(selectedGroup.id, viewAssetId);
            } else {
                logRes = await apiService.getPortfolioApprovalLog(selectedGroup.id);
            }
            if (logRes?.ok) {
                const logData = await logRes.json();
                setApprovalLog(logData.entries || []);
                setLogTotalCount(logData.total_count || 0);
            }
        } finally {
            setLoading(false);
            setLoadingLog(false);
        }
    };

    const fetchPendingChanges = async (assetId: number) => {
        if (pendingChanges[assetId]) {
            setExpandedPendingAssetId(expandedPendingAssetId === assetId ? null : assetId);
            return;
        }
        setLoadingPending(assetId);
        setExpandedPendingAssetId(assetId);
        try {
            const response = await apiService.getPortfolioAssetHistory(selectedGroup!.id, assetId);
            if (response?.ok) {
                const data = await response.json();
                setPendingChanges(prev => ({ ...prev, [assetId]: data }));
            }
        } finally {
            setLoadingPending(null);
        }
    };

    const handleApproveAsset = async (assetId: number) => {
        if (!selectedGroup) return;
        setRefreshingAssetId(assetId);
        try {
            await apiService.refreshPortfolioAsset(selectedGroup.id, assetId);
            // Clear cached pending changes and refresh data
            setPendingChanges(prev => { const next = { ...prev }; delete next[assetId]; return next; });
            // Refresh: staleness panel, the group (for pendingRecomputeCount derivation),
            // and the groups list (so the sidebar's is_any_stale indicator stays in sync).
            await Promise.all([fetchData(), selectGroup(selectedGroup.id), refreshGroups()]);
        } finally {
            setRefreshingAssetId(null);
        }
    };

    const handleRefreshAllAndCompute = async () => {
        await computeGroup();
        onClose();
    };

    if (!selectedGroup) return null;

    const staleAssets = (staleness?.assets || []).filter((a: any) => a.is_stale);
    const filteredStaleAssets = viewAssetId ? staleAssets.filter((a: any) => a.asset_id === viewAssetId) : staleAssets;

    // Find asset name for the filter
    const filteredAsset = viewAssetId ? selectedGroup.assets.find((a: any) => a.id === viewAssetId) : null;
    const filteredAssetLabel = filteredAsset
        ? `${filteredAsset.model_name || 'Model'} — ${filteredAsset.company_name || 'Company'}`
        : null;

    return (
        <div className="fixed inset-0 bg-black/50 flex justify-end z-50" onClick={onClose}>
            <div className="bg-white w-full max-w-2xl h-full flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="p-5 border-b border-slate-200 shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Portfolio History</h2>
                            <p className="text-sm text-slate-500 mt-0.5">Approval audit trail & pending changes</p>
                        </div>
                        <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400">
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </div>

                    {/* View toggle */}
                    <div className="flex items-center gap-2 mt-3">
                        <button
                            onClick={() => setViewAssetId(null)}
                            className={cn(
                                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                                !viewAssetId ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            )}
                        >
                            Group History
                        </button>
                        {filteredAssetLabel && (
                            <button
                                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white"
                            >
                                Asset: {filteredAssetLabel}
                            </button>
                        )}
                        {viewAssetId && (
                            <button
                                onClick={() => setViewAssetId(null)}
                                className="p-1 rounded hover:bg-slate-100 text-slate-400"
                                title="Clear filter"
                            >
                                <span className="material-symbols-outlined text-[16px]">close</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto p-5 space-y-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* ── Pending Changes (stale assets) ── */}
                            {filteredStaleAssets.length > 0 && (
                                <section>
                                    <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <span className="material-symbols-outlined text-[14px]">warning</span>
                                        Pending changes — awaiting approval
                                    </h3>
                                    <div className="space-y-2">
                                        {filteredStaleAssets.map((asset: any) => (
                                            <div key={asset.asset_id} className="border border-amber-200 rounded-lg overflow-hidden bg-amber-50/50">
                                                <button
                                                    onClick={() => fetchPendingChanges(asset.asset_id)}
                                                    className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-amber-50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                                                        <div>
                                                            <div className="text-sm font-medium text-slate-900">
                                                                {asset.model_name || `Model ${asset.model_id}`}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {asset.scenario_name} {asset.company_name ? `— ${asset.company_name}` : ''}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-amber-700 font-medium">
                                                            {asset.changes_count} change{asset.changes_count !== 1 ? 's' : ''}
                                                        </span>
                                                        <span className={cn(
                                                            "material-symbols-outlined text-[18px] text-slate-400 transition-transform",
                                                            expandedPendingAssetId === asset.asset_id && "rotate-180"
                                                        )}>expand_more</span>
                                                    </div>
                                                </button>

                                                {expandedPendingAssetId === asset.asset_id && (
                                                    <div className="border-t border-amber-200 bg-white">
                                                        {loadingPending === asset.asset_id ? (
                                                            <div className="flex justify-center py-6">
                                                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <ChangesList changes={pendingChanges[asset.asset_id] || []} />
                                                                {canWrite && (
                                                                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
                                                                        <button
                                                                            onClick={() => handleApproveAsset(asset.asset_id)}
                                                                            disabled={refreshingAssetId === asset.asset_id}
                                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                                                                        >
                                                                            {refreshingAssetId === asset.asset_id ? (
                                                                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                                            ) : (
                                                                                <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                                                            )}
                                                                            Approve changes
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* ── Approval History ── */}
                            <section>
                                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="material-symbols-outlined text-[14px]">history</span>
                                    Approval history ({logTotalCount})
                                </h3>

                                {loadingLog ? (
                                    <div className="flex justify-center py-8">
                                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : approvalLog.length === 0 ? (
                                    <div className="text-center py-8 text-slate-400 text-sm">
                                        No approvals recorded yet. Changes will appear here after review.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {approvalLog.map((entry: any) => (
                                            <div key={entry.id} className="border border-slate-200 rounded-lg overflow-hidden">
                                                <button
                                                    onClick={() => setExpandedApprovalId(expandedApprovalId === entry.id ? null : entry.id)}
                                                    className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                                                >
                                                    <UserInitials name={entry.user_name} />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm text-slate-900">
                                                            <span className="font-medium">{entry.user_name}</span>
                                                            <span className="text-slate-500"> approved </span>
                                                            <span className="font-medium">{entry.model_name}</span>
                                                            {entry.company_name && (
                                                                <span className="text-slate-500"> — {entry.company_name}</span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-slate-400 mt-0.5">
                                                            {parseServerTimestamp(entry.approved_at)?.toLocaleString()}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {entry.changes_count > 0 && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                                                {entry.changes_count} change{entry.changes_count !== 1 ? 's' : ''}
                                                            </span>
                                                        )}
                                                        {entry.changes_count === 0 && (
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600">
                                                                No changes
                                                            </span>
                                                        )}
                                                        <span className={cn(
                                                            "material-symbols-outlined text-[18px] text-slate-400 transition-transform",
                                                            expandedApprovalId === entry.id && "rotate-180"
                                                        )}>expand_more</span>
                                                    </div>
                                                </button>

                                                {expandedApprovalId === entry.id && (
                                                    <div className="border-t border-slate-100 bg-slate-50">
                                                        {!viewAssetId && (
                                                            <div className="px-4 py-2 text-xs flex justify-end border-b border-slate-100">
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); setViewAssetId(entry.asset_id); }}
                                                                    className="text-blue-600 hover:text-blue-700 font-medium"
                                                                >
                                                                    View asset history
                                                                </button>
                                                            </div>
                                                        )}
                                                        <ChangesList changes={entry.changes_summary || []} />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-slate-200 flex items-center justify-between shrink-0 bg-white">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                        Close
                    </button>
                    {canWrite && filteredStaleAssets.length > 0 && (
                        <button
                            onClick={handleRefreshAllAndCompute}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            <span className="material-symbols-outlined text-[18px]">refresh</span>
                            Approve All & Recompute
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}


/** Reusable component to display a list of scenario changes */
function ChangesList({ changes }: { changes: any[] }) {
    const numberLocale = useNumberLocale();
    if (!changes || changes.length === 0) {
        return <p className="text-sm text-slate-400 text-center py-4">No change details available</p>;
    }

    return (
        <div className="max-h-[250px] overflow-auto divide-y divide-slate-100">
            {changes.map((event: any, idx: number) => {
                const config = EVENT_TYPE_LABELS[event.type] || { label: event.type, icon: 'info', color: 'text-slate-500' };
                return (
                    <div key={event.id || idx} className="px-4 py-2.5 flex items-start gap-3">
                        <span className={cn("material-symbols-outlined text-[16px] mt-0.5", config.color)}>
                            {config.icon}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-900">
                                <span className="font-medium">{config.label}</span>
                                {event.model_data_name && (
                                    <span className="text-slate-500"> on {snakeToPascal(event.model_data_name)}</span>
                                )}
                                {event.model_data_value_name && (
                                    <span className="text-slate-500"> / {snakeToPascal(event.model_data_value_name)}</span>
                                )}
                            </div>
                            {(event.old_value !== undefined && event.old_value !== null && event.new_value !== undefined && event.new_value !== null) && (
                                <div className="text-xs text-slate-500 mt-0.5">
                                    <span className="line-through text-red-400">{formatValue(event.old_value, numberLocale)}</span>
                                    <span className="mx-1">→</span>
                                    <span className="text-green-600 font-medium">{formatValue(event.new_value, numberLocale)}</span>
                                </div>
                            )}
                            <div className="text-xs text-slate-400 mt-0.5">
                                {event.user_name && <span>{event.user_name} — </span>}
                                {event.date && parseServerTimestamp(event.date)?.toLocaleString()}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
